<script setup lang="ts">
import { RelationshipToPerson } from '@/models/RelationshipToPerson';
import KpDropdown from '@kp/geofy-vuejs-lib/tg-system/components/KpDropdown.vue';
import KpDropdownAutocomplete from '@kp/geofy-vuejs-lib/tg-system/components/KpDropdownAutocomplete.vue';
import {
    createValidator,
    createValidators,
    validateNotEmpty,
    validateMaxLength,
    validateEmail,
    validateNumberMin,
    validateNumberMax
} from '@kp/geofy-vuejs-lib/tools/model_validators.js';
import { instance as api } from '@/helpers/api';
import { ROLE } from '@/models/RelationshipToPerson';
import { computed, onMounted, ref, watch, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOrtCollection } from '@/composables/useOrtCollection';
import { useCountryCollection } from '@/composables/useCountryCollection';
import type { UseCollectionOptions } from '@/composables/useDataTableProps';
import type { KTGDropdownAutocompleteChangeEvent } from '@kanton-thurgau/design-system/dropdown';
import { Ort } from '@/models/Ort';
import { Country } from '@/models/Country';

const address = defineModel<RelationshipToPerson>({ default: null });

const props = withDefaults(
    defineProps<{
        title?: string | null;
        smallTitle?: string | null;
        optionalFields?: boolean;
        noLine?: boolean;
        withRole?: boolean;
        deletable: boolean;
        readonly: boolean;
        forceValidation?: boolean;
        orgContactOptional?: boolean;
        addAllowed?: boolean;
        parzellen?: string[] | null;
        hideEndLine?: boolean;
        organisationOnly?: boolean;
        organisationTitle?: string;
        organisationPlaceholder?: string;
        typeLabel?: string;
    }>(),
    {
        title: null,
        smallTitle: null,
        optionalFields: false,
        deletable: false,
        noLine: false,
        withRole: false,
        readonly: false,
        forceValidation: false,
        orgContactOptional: false,
        addAllowed: false,
        parzellen: null,
        hideEndLine: false,
        organisationOnly: false,
        organisationTitle: () => useI18n().t('organisation_firma'),
        organisationPlaceholder: () => useI18n().t('organisation_placeholder')
    }
);
const emit = defineEmits(['delete', 'valid', 'add']);

const { allValid, validators, validateAll } = createValidators({
    organisation_name: createValidator(
        computed(() => address.value?.organisation_name),
        [validateNotEmpty, validateMaxLength(255)],
        { initialValidate: false }
    ),
    first_name: createValidator(
        computed(() => address.value?.first_name),
        [
            (val) => {
                if (address.value?.address_type === 'organisation' && props.orgContactOptional) {
                    return validateMaxLength(255)(val);
                } else {
                    return validateMaxLength(255)(val) && validateNotEmpty(val);
                }
            }
        ],
        { initialValidate: false }
    ),
    last_name: createValidator(
        computed(() => address.value?.last_name),
        [
            (val) => {
                if (address.value?.address_type === 'organisation' && props.orgContactOptional) {
                    return validateMaxLength(255)(val);
                } else {
                    return validateMaxLength(255)(val) && validateNotEmpty(val);
                }
            }
        ],
        { initialValidate: false }
    ),
    street: createValidator(
        computed(() => address.value?.street),
        [validateNotEmpty, validateMaxLength(255)],
        { initialValidate: false }
    ),
    house_number: createValidator(
        computed(() => address.value?.house_number),
        [validateNotEmpty, validateMaxLength(255)],
        { initialValidate: false }
    ),
    swiss_zip_code: createValidator(
        computed(() => address.value?.swiss_zip_code),
        [validateNotEmpty, validateNumberMax(9999), validateNumberMin(1000)],
        { initialValidate: false }
    ),
    foreign_zip_code: createValidator(
        computed(() => address.value?.foreign_zip_code),
        [validateNotEmpty, validateMaxLength(10)],
        { initialValidate: false }
    ),
    town: createValidator(
        computed(() => address.value?.town),
        [validateNotEmpty, validateMaxLength(255)],
        { initialValidate: false }
    ),
    email: createValidator(
        computed(() => address.value?.email),
        props.optionalFields ? [validateEmail] : [validateNotEmpty, validateEmail],
        { initialValidate: false }
    ),
    phone: createValidator(
        computed(() => address.value?.phone),
        props.optionalFields ? [validateMaxLength(255)] : [validateNotEmpty, validateMaxLength(255)],
        { initialValidate: false }
    )
});

const roles = ref([
    { key: ROLE.PROJECT_AUTHOR, text: useI18n().t(ROLE.PROJECT_AUTHOR) },
    { key: ROLE.LANDOWNER, text: useI18n().t(ROLE.LANDOWNER) },
    { key: ROLE.APPLICANT, text: useI18n().t(ROLE.APPLICANT) }
]);

function onOrtSelect(value: KTGDropdownAutocompleteChangeEvent) {
    if (value?.value) {
        let [plz, ortsname] = value.value.split('::');
        address.value.swiss_zip_code = Number(plz);
        address.value.town = ortsname;
    } else {
        address.value.swiss_zip_code = null;
        address.value.town = null;
    }
}

const townSearchQuery: Ref<string> = ref('');
const { collection: orte, query: queryOrte } = useOrtCollection({
    autoload: false
} as UseCollectionOptions);

const townLoading = ref(false);
async function onTownSearchQueryChange(queryStr: string | undefined) {
    orte.setQueryParam('query', queryStr?.trim() || null);
    townLoading.value = true;
    await queryOrte({
        // append the search field as query to the request:
        query: townSearchQuery?.value?.trim() || null
    });
    townLoading.value = false;
}
watch(townSearchQuery, onTownSearchQueryChange);

const countrySearchQuery: Ref<string> = ref('');
const { collection: countries, query: queryCountries } = useCountryCollection({
    autoload: false
} as UseCollectionOptions);

const loading = ref(false);
async function onCountrySearchQueryChange(queryStr: string | undefined) {
    countries.setQueryParam('query', queryStr?.trim() || null);
    loading.value = true;
    await queryCountries({
        // append the search field as query to the request:
        query: countrySearchQuery?.value?.trim() || null
    });
    loading.value = false;
}
watch(countrySearchQuery, onCountrySearchQueryChange);

watch(allValid, (val) => {
    emit('valid', val);
});

function mutateRecord(field: string, value: any) {
    if (props.readonly) {
        return;
    }
    address?.value?.set(field, value);
}

async function getCH() {
    const res = await api().get('/Country/getCH');
    return res.data.data;
}

async function getCountry(id: number | null, iso2Code: string | null) {
    const res = await api().post('/Country/getByProp', {
        id,
        iso2Code
    });
    return res.data.records;
}

let ch: Ref<Country | null> = ref(null);

function countryIdChanged(countryId: any) {
    address.value.country_id = Number(countryId);
    address.value.town = null;
    if (address.value.country_id === ch?.value?.id) {
        address.value.foreign_zip_code = null;
    } else {
        address.value.swiss_zip_code = null;
    }
}

onMounted(async () => {
    if (address.value.zip_town_ident) {
        let ort = new Ort();
        ort.ident = address.value.zip_town_ident;
        ort.plz = address.value.swiss_zip_code;
        ort.ortschaftsname = address.value.town;
        ort.plz_ort = address.value.zip_town;
        ort.id = Number(address.value.zip_town_ident.split('::')[2]);
        orte.push(ort);
    }
    ch.value = await getCH();
    if (!address.value.country_id) {
        if (ch.value) {
            countries.push(ch.value);
            address.value.country_id = ch?.value?.id;
        }
    } else {
        const country = await getCountry(address.value.country_id, null);
        if (country) {
            countries.push(country);
        }
    }

    if (props.forceValidation) {
        validateAll();
    }
});
</script>

<template>
    <div>
        <h5 v-if="title" class="ktg-title-05 mt-5rem ktg-inner-box-padding">{{ props.title }}</h5>
        <ktg-label v-if="props.smallTitle" class="ktg-inner-box-padding">{{ props.smallTitle }}</ktg-label>
        <ktg-line v-if="!props.noLine" class="mt-0_5rem mb-1rem"></ktg-line>
        <div v-if="props.withRole" class="flex flex-row align-items-end gap-3">
            <ktg-field class="flex-1">
                <ktg-label for="role">{{ $t('role') }}</ktg-label>
                <KpDropdown
                    v-model="address.role"
                    :options="roles"
                    display-field="text"
                    value-field="key"
                    class="w-full"
                    :readonly="readonly"
                    :emit-value-cast="String"
                ></KpDropdown>
            </ktg-field>
            <div class="flex-1"></div>
        </div>
        <div class="form-grid mb-0">
            <div v-if="!props.organisationOnly" class="col-12">
                <div class="ktg-inner-box-padding" v-if="props.typeLabel">{{ props.typeLabel }} *</div>
                <ktg-labelled-choice class="ktg-inner-box-padding mt-1rem">
                    <ktg-radio
                        :checked="address.address_type === 'person'"
                        :input-id="`${address.id}-person`"
                        :name="`${address.id}-person`"
                        value="person"
                        :readonly="readonly"
                        @change="
                            () => {
                                mutateRecord('address_type', 'person');
                                validators.first_name.validate();
                                validators.last_name.validate();
                            }
                        "
                    ></ktg-radio>
                    <ktg-label :for="`${address.id}-person`">{{ $t('natuerliche_person') }}</ktg-label>
                </ktg-labelled-choice>
                <ktg-labelled-choice class="ktg-inner-box-padding mt-0_5rem mb-0_5rem">
                    <ktg-radio
                        :checked="address.address_type === 'organisation'"
                        :input-id="`${address.id}-organisation`"
                        :name="`${address.id}-organisation`"
                        value="organisation"
                        :readonly="readonly"
                        @change="
                            () => {
                                mutateRecord('address_type', 'organisation');
                                validators.first_name.validate();
                                validators.last_name.validate();
                            }
                        "
                    ></ktg-radio>
                    <ktg-label :for="`${address.id}-organisation`">{{ $t('firma_oder_organisation') }}</ktg-label>
                </ktg-labelled-choice>
            </div>
            <ktg-field v-if="address.address_type === 'organisation'" class="mt-0_5rem col-12">
                <ktg-label for="organisation_name">{{ organisationTitle + ' *' }}</ktg-label>
                <ktg-text-input
                    class="w-full"
                    :value="address.organisation_name"
                    input-id="organisation_name"
                    type="text"
                    :placeholder="organisationPlaceholder"
                    error-id="err_organisation_name"
                    :error="!validators.organisation_name.isValid.value"
                    required
                    :readonly="readonly"
                    @change="mutateRecord('organisation_name', $event.target.value)"
                    @blur="validators.organisation_name.validate()"
                />
                <ktg-field-error v-if="!validators.organisation_name.isValid.value" id="err_organisation_name">{{
                    validators.organisation_name.msg.value
                }}</ktg-field-error>
            </ktg-field>
            <ktg-field class="mt-0_5rem col-12 md:col-6">
                <ktg-label for="first_name">{{
                    $t('vorname') +
                    (address.address_type === 'organisation' ? ' ' + $t('ansprechperson') : '') +
                    (address.address_type !== 'organisation' || !props.orgContactOptional ? ' *' : '')
                }}</ktg-label>
                <ktg-text-input
                    class="w-full"
                    :value="address.first_name"
                    input-id="first_name"
                    type="text"
                    :placeholder="$t('vorname_placeholder')"
                    error-id="err_first_name"
                    :error="!validators.first_name.isValid.value"
                    :required="address.address_type !== 'organisation' || !props.orgContactOptional"
                    :readonly="readonly"
                    @change="mutateRecord('first_name', $event.target.value)"
                    @blur="validators.first_name.validate()"
                />
                <ktg-field-error v-if="!validators.first_name.isValid.value" id="err_first_name">{{
                    validators.first_name.msg.value
                }}</ktg-field-error>
            </ktg-field>
            <ktg-field class="mt-0_5rem col-12 md:col-6">
                <ktg-label for="last_name">{{
                    $t('nachname') +
                    (address.address_type === 'organisation' ? ' ' + $t('ansprechperson') : '') +
                    (address.address_type !== 'organisation' || !props.orgContactOptional ? ' *' : '')
                }}</ktg-label>
                <ktg-text-input
                    class="w-full"
                    :value="address.last_name"
                    input-id="last_name"
                    type="text"
                    :placeholder="$t('nachname_placeholder')"
                    :required="address.address_type !== 'organisation' || !props.orgContactOptional"
                    :readonly="readonly"
                    error-id="err_last_name"
                    :error="!validators.last_name.isValid.value"
                    @change="mutateRecord('last_name', $event.target.value)"
                    @blur="validators.last_name.validate()"
                />
                <ktg-field-error v-if="!validators.last_name.isValid.value" id="err_last_name">{{
                    validators.last_name.msg.value
                }}</ktg-field-error>
            </ktg-field>
            <ktg-field class="mt-0_5rem col-12 md:col-10">
                <ktg-label for="street">{{ $t('strasse') + ' *' }}</ktg-label>
                <ktg-text-input
                    class="w-full"
                    :value="address.street"
                    input-id="street"
                    type="text"
                    required
                    :readonly="readonly"
                    :placeholder="$t('strasse_placeholder')"
                    error-id="err_street"
                    :error="!validators.street.isValid.value"
                    @change="mutateRecord('street', $event.target.value)"
                    @blur="validators.street.validate()"
                />
                <ktg-field-error v-if="!validators.street.isValid.value" id="err_street">{{
                    validators.street.msg.value
                }}</ktg-field-error>
            </ktg-field>
            <ktg-field class="mt-0_5rem col-12 md:col-2">
                <ktg-label for="house_number">{{ $t('number_short') + ' *' }}</ktg-label>
                <ktg-text-input
                    class="w-full"
                    :value="address.house_number"
                    input-id="house_number"
                    type="text"
                    required
                    :placeholder="$t('house_number_placeholder')"
                    :readonly="readonly"
                    error-id="err_house_number"
                    :error="!validators.house_number.isValid.value"
                    @change="mutateRecord('house_number', $event.target.value)"
                    @blur="validators.house_number.validate()"
                />
                <ktg-field-error v-if="!validators.house_number.isValid.value" id="err_house_number">{{
                    validators.house_number.msg.value
                }}</ktg-field-error>
            </ktg-field>
            <ktg-field v-if="address.country_id === ch?.id" class="mt-0_5rem col-12">
                <ktg-label for="plz_ort">{{ $t('plz_ort') + ' *' }}</ktg-label>
                <KpDropdownAutocomplete
                    v-model="address.zip_town_ident"
                    :loading="loading"
                    :options="orte.getModels()"
                    display-field="plz_ort"
                    value-field="ident"
                    class="w-full"
                    :emit-value-cast="String"
                    trailing-icon="search"
                    filter-strategy="manual"
                    error-id="err_swiss_zip_code"
                    :placeholder="$t('plz_ort_placeholder')"
                    :error="!validators.swiss_zip_code.isValid.value"
                    :readonly="readonly"
                    @search-input="townSearchQuery = $event.searchTerm"
                    @change="onOrtSelect"
                    @blur="validators.swiss_zip_code.validate()"
                ></KpDropdownAutocomplete>
                <ktg-field-error v-if="!validators.swiss_zip_code.isValid.value" id="err_swiss_zip_code">{{
                    validators.swiss_zip_code.msg.value
                }}</ktg-field-error>
            </ktg-field>
            <template v-else>
                <ktg-field class="mt-0_5rem col-12 md:col-6">
                    <ktg-label for="foreign_zip_code">{{ $t('plz') + ' *' }}</ktg-label>
                    <ktg-text-input
                        class="w-full"
                        :value="address.foreign_zip_code"
                        input-id="foreign_zip_code"
                        type="text"
                        required
                        :placeholder="$t('foreign_zip_code_placeholder')"
                        error-id="err_foreign_zip_code"
                        :error="!validators.foreign_zip_code.isValid.value"
                        :readonly="readonly"
                        @change="mutateRecord('foreign_zip_code', $event.target.value)"
                        @blur="validators.foreign_zip_code.validate()"
                    />
                    <ktg-field-error v-if="!validators.foreign_zip_code.isValid.value" id="err_foreign_zip_code">{{
                        validators.foreign_zip_code.msg.value
                    }}</ktg-field-error>
                </ktg-field>
                <ktg-field class="mt-0_5rem col-12 md:col-6">
                    <ktg-label for="town">{{ $t('ort') + ' *' }}</ktg-label>
                    <ktg-text-input
                        class="w-full"
                        :value="address.town"
                        :placeholder="$t('town_placeholder')"
                        input-id="town"
                        type="text"
                        required
                        :readonly="readonly"
                        error-id="err_town"
                        :error="!validators.town.isValid.value"
                        @change="mutateRecord('town', $event.target.value)"
                        @blur="validators.town.validate()"
                    />
                    <ktg-field-error v-if="!validators.town.isValid.value" id="err_town">{{
                        validators.town.msg.value
                    }}</ktg-field-error>
                </ktg-field>
            </template>
            <div class="w-full">
                <ktg-field class="mt-0_5rem col-12 md:col-6">
                    <ktg-label for="country">{{ $t('country') + ' *' }}</ktg-label>
                    <KpDropdownAutocomplete
                        v-model="address.country_id"
                        :loading="loading"
                        :options="countries.getModels()"
                        display-field="short_name_de"
                        value-field="id"
                        class="w-full"
                        :emit-value-cast="String"
                        trailing-icon="search"
                        filter-strategy="manual"
                        :readonly="readonly"
                        @search-input="countrySearchQuery = $event.searchTerm"
                        @change="countryIdChanged($event.target.value)"
                    ></KpDropdownAutocomplete>
                </ktg-field>
            </div>
            <ktg-field class="mt-0_5rem col-12 md:col-6">
                <ktg-label for="emailaddress">{{ $t('emailaddress') + (props.optionalFields ? '' : ' *') }}</ktg-label>
                <ktg-text-input
                    class="w-full"
                    :value="address.email"
                    input-id="emailaddress"
                    type="text"
                    :required="!props.optionalFields"
                    :placeholder="$t('email_placeholder')"
                    error-id="err_email"
                    :error="!validators.email.isValid.value"
                    :readonly="readonly"
                    @change="mutateRecord('email', $event.target.value)"
                    @blur="validators.email.validate()"
                />
                <ktg-field-error v-if="!validators.email.isValid.value" id="err_email">{{
                    validators.email.msg.value
                }}</ktg-field-error>
            </ktg-field>
            <ktg-field class="mt-0_5rem col-12 md:col-6">
                <ktg-label for="phone-input">{{ $t('telefon') + (props.optionalFields ? '' : ' *') }}</ktg-label>
                <ktg-phone-input
                    class="w-full"
                    id="phone-input"
                    :value="address.phone"
                    :required="!props.optionalFields"
                    :placeholder="$t('telefon_placeholder')"
                    error-id="err_phone"
                    :error="!validators.phone.isValid.value"
                    @change="mutateRecord('phone', $event.target.value)"
                    :readonly="readonly"
                    @blur="validators.phone.validate()"
                />
                <ktg-field-error v-if="!validators.phone.isValid.value" id="err_phone">{{
                    validators.phone.msg.value
                }}</ktg-field-error>
            </ktg-field>
        </div>
        <ktg-line v-if="!props.hideEndLine" class="mt-0_5rem mb-1_5rem"></ktg-line>
        <div class="form-grid">
            <ktg-button
                v-if="props.deletable && !props.readonly"
                class="flex-1"
                size="small"
                appearance="danger"
                :readonly="readonly"
                @click="emit('delete')"
                trailing-icon="trash-delete"
                >{{ $t('remove_entry') }}</ktg-button
            >
            <div v-else class="flex-1"></div>
            <ktg-button
                v-if="props.addAllowed"
                class="flex-1"
                size="small"
                appearance="secondary"
                :readonly="readonly"
                trailing-icon="plus"
                @click="emit('add')"
            >
                {{ $t('add_role', { role: $t(address.role || '') }) }}
            </ktg-button>
            <div v-else class="flex-1"></div>
        </div>
    </div>
</template>
